import * as BABYLON from '@babylonjs/core';
import { ActionManager, Color3, ExecuteCodeAction } from '@babylonjs/core';
import * as Event from '../event';
import * as R from '../renderer';
import { addModel, scene as rendererScene, scene } from '../renderer';
import * as Transform from '../transform';
export const isModularFurniture = (x) => {
    if (typeof x === 'object' && x !== null && x.hasOwnProperty('_tag')) {
        return x._tag === 'ModularFurniture';
    }
    else
        return false;
};
const addModularParticleModel = async (renderer, piece, parentTransform) => {
    // create a root node and a delta origin node for offset position + rotation
    const particleTransform = Transform.createForParticle();
    particleTransform.id = piece.id;
    particleTransform.metadata = {
        instanceId: piece.id,
    };
    const _scene = rendererScene(renderer);
    // attach the meshes to the delta origin node
    try {
        await addModel(renderer, particleTransform, piece.modelUrl);
    }
    catch {
        throw new Error(`Failed to fetch 3d model with id: ${piece.id}`);
    }
    // parent the new particle transform if it's still valid (async operation to fetch, may have been removed!)
    if (parentTransform && !parentTransform.isDisposed()) {
        particleTransform.parent = parentTransform;
    }
    else {
        particleTransform.dispose();
        return particleTransform;
    }
    // set root node to raw position from attachment point
    let { x: px, y: py, z: pz } = piece.position;
    let { w: rw, x: rx, y: ry, z: rz } = piece.orientation;
    particleTransform.position = new BABYLON.Vector3(px, py, pz);
    particleTransform.rotationQuaternion = new BABYLON.Quaternion(rx, ry, rz, rw);
    // return root node
    return particleTransform;
};
const getChildMeshes = (t) => {
    return t.getChildMeshes(false); // needs to be casted because highlight layer doesn't accept AbstractMesh
};
export const render = async (renderer, furniture) => {
    const _scene = scene(renderer);
    // TODO: dispose existing scene only when ready to display replacement.
    Transform.disposeType(Transform.Type.SINGLE_PARTICLE_TRANSFORM)(_scene.transformNodes);
    const allOldTransforms = Transform.findAllRootFurniture(_scene.transformNodes);
    const pieces = furniture.pieces;
    const newTransform = Transform.createForRootFurniture();
    newTransform.setEnabled(false);
    const pieceLoading = pieces.map(async (piece, moduleIdx) => {
        const particleTransform = await addModularParticleModel(renderer, piece, newTransform);
        let selected = furniture.selectedPiece === moduleIdx;
        const actionManager = new ActionManager(_scene);
        const meshes = getChildMeshes(particleTransform);
        actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger, () => {
            const events = R.events(renderer);
            selected = true;
            meshes.forEach(addSelectedOverlay); // Pre-emptively add the selected overlay. It'll get replaced immediately but this avoids some jank
            events.emit(Event.Kind.OnFurnitureModuleClick, furniture.instanceId, moduleIdx);
        }));
        actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, (e) => {
            if (e.sourceEvent.pointerType === 'mouse' && !selected) {
                meshes.forEach(addHoverOverlay);
            }
        }));
        actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, (e) => {
            if (e.sourceEvent.pointerType === 'mouse' && !selected) {
                meshes.forEach(removeOverlay);
            }
        }));
        meshes.forEach((child) => {
            child.actionManager = actionManager;
        });
        if (selected) {
            meshes.forEach(addSelectedOverlay);
        }
        return particleTransform;
    });
    await Promise.all(pieceLoading);
    allOldTransforms.forEach(transform => transform.dispose(false, true));
    newTransform.setEnabled(true);
    return { newTransform };
};
const addHoverOverlay = (m) => {
    m.renderOverlay = true;
    m.overlayAlpha = 0.15;
    m.overlayColor = Color3.White();
    (m.metadata = m.metadata ?? {}).overlayState = 'hover';
};
const addSelectedOverlay = (m) => {
    m.renderOverlay = true;
    m.overlayAlpha = 0.25;
    m.overlayColor = Color3.White();
    (m.metadata = m.metadata ?? {}).overlayState = 'selected';
};
const removeOverlay = (m) => {
    m.renderOverlay = false;
    (m.metadata = m.metadata ?? {}).overlayState = false;
};
export const suspendHoverOverlay = (m) => {
    if (m.metadata?.overlayState === 'hover') {
        m.renderOverlay = false;
    }
};
export const restoreHoverOverlay = (m) => {
    m.renderOverlay = m.metadata?.overlayState === 'hover';
};
